import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export interface RoomSelection {
  room: any;
  roomType: string;
  roomCode: string;
  qtyAdults: number; // Cambiado de adults a qtyAdults
  qtyChilds: number; // Cambiado de children a qtyChilds
  childsage: number[]; // Cambiado de childrenAges a childsage
  satisfiedConfigIndex?: number;
}

export interface HotelRoomState {
  originalConfiguration: any[];
  selectedRooms: RoomSelection[];
  remainingConfigs: any[];
  satisfiedConfigs: any[];
}

@Injectable({
  providedIn: 'root'
})
export class HotelService {
  private readonly STORAGE_KEY = 'hotel_rooms_state';

  private stateSubject = new BehaviorSubject<HotelRoomState | null>(this.getStoredState());
  public state$ = this.stateSubject.asObservable();

  /**
   * Inicializa el estado basado en la configuración original
   */
  initializeSelection(formData: any): void {
    if (!formData) {
      console.error('No se encontró roomConfiguration en formData');
      return;
    }

    const roomConfig = formData;

    const state: HotelRoomState = {
      originalConfiguration: roomConfig,
      selectedRooms: [],
      remainingConfigs: [...roomConfig],
      satisfiedConfigs: []
    };

    this.saveState(state);
    this.stateSubject.next(state);
  }

  /**
   * Agrega una habitación seleccionada
   */
  addSelectedRoom(room: any): boolean {
    const current = this.stateSubject.value;
    if (!current) return false;

    const roomAdults = Number(room.adults) || 0;
    const roomChildren = Number(room.childrens) || 0;

    // Buscar una configuración que coincida con esta habitación
    const configIndex = this.findMatchingConfig(current.remainingConfigs, roomAdults, roomChildren);

    if (configIndex === -1) {
      console.warn('No hay configuración disponible que coincida con esta habitación');
      return false;
    }

    const matchedConfig = current.remainingConfigs[configIndex];

    // Crear la selección con los nombres correctos
    const roomSelection: RoomSelection = {
      room: room,
      roomType: room.roomType,
      roomCode: room.roomCode,
      qtyAdults: roomAdults,
      qtyChilds: roomChildren,
      childsage: matchedConfig.children || matchedConfig.childsage || [],
      satisfiedConfigIndex: configIndex
    };

    // Actualizar estado
    const newRemainingConfigs = [...current.remainingConfigs];
    const [satisfiedConfig] = newRemainingConfigs.splice(configIndex, 1);

    const newState: HotelRoomState = {
      ...current,
      selectedRooms: [...current.selectedRooms, roomSelection],
      remainingConfigs: newRemainingConfigs,
      satisfiedConfigs: [...current.satisfiedConfigs, satisfiedConfig]
    };

    this.saveState(newState);
    this.stateSubject.next(newState);
    return true;
  }

  /**
   * Encuentra una configuración que coincida con los parámetros de la habitación
   */
  private findMatchingConfig(configs: any[], roomAdults: number, roomChildren: number): number {
    return configs.findIndex(config => {
      // Usar qtyAdults y qtyChilds para la comparación
      const configAdults = Number(config.qtyAdults) || config.adults || 0;
      const configChildren = Number(config.qtyChilds) || config.children?.length || 0;

      return configAdults === roomAdults && configChildren === roomChildren;
    });
  }

  /**
   * Remueve una habitación seleccionada
   */
  /**
   * Remueve una habitación seleccionada
   */
  removeSelectedRoom(item: any): void {
    const current = this.stateSubject.value;
    if (!current) return;

    // Buscar por productKey que es único
    const roomToRemove = current.selectedRooms.find(selection =>
      selection.room.productKey === item.productKey
    );

    if (!roomToRemove) {
      console.warn('Room to remove NOT FOUND in selectedRooms');
      return;
    }

    // Usar satisfiedConfigIndex que ya tenemos guardado
    if (roomToRemove.satisfiedConfigIndex !== undefined && roomToRemove.satisfiedConfigIndex < current.satisfiedConfigs.length) {
      const configToRestore = current.satisfiedConfigs[roomToRemove.satisfiedConfigIndex];

      const newState: HotelRoomState = {
        ...current,
        selectedRooms: current.selectedRooms.filter(selection =>
          selection.room.productKey !== item.productKey
        ),
        remainingConfigs: [...current.remainingConfigs, configToRestore],
        satisfiedConfigs: current.satisfiedConfigs.filter((_, index) => index !== roomToRemove.satisfiedConfigIndex)
      };

      this.saveState(newState);
      this.stateSubject.next(newState);
    }
  }

  /**
   * Verifica si se completó la selección
   */
  isSelectionComplete(): boolean {
    const current = this.stateSubject.value;
    if (!current) return false;

    return current.remainingConfigs.length === 0;
  }

  /**
   * Obtiene todas las habitaciones seleccionadas
   */
  getSelectedRooms(): any[] {
    const current = this.stateSubject.value;
    return current?.selectedRooms.map(selection => selection.room) || [];
  }

  /**
   * Obtiene las configuraciones que aún necesitan habitación
   */
  getRemainingConfigs(): any[] {
    const current = this.stateSubject.value;
    return current?.remainingConfigs || [];
  }

  /**
   * Obtiene las configuraciones que ya tienen habitación
   */
  getSatisfiedConfigs(): any[] {
    const current = this.stateSubject.value;
    return current?.satisfiedConfigs || [];
  }

  /**
   * Obtiene el estado completo
   */
  getCurrentState(): HotelRoomState | null {
    return this.stateSubject.value;
  }

  /**
   * Verifica si una habitación puede satisfacer alguna configuración restante
   */
  canSelectRoom(room: any): boolean {
    const current = this.stateSubject.value;
    if (!current) return false;

    const roomAdults = room.adults || 0;
    const roomChildren = room.childrens || 0;

    return this.findMatchingConfig(current.remainingConfigs, roomAdults, roomChildren) !== -1;
  }

  /**
   * Obtiene las configuraciones que coinciden con una habitación específica
   */
  getMatchingConfigs(room: any): any[] {
    const current = this.stateSubject.value;
    if (!current) return [];

    const roomAdults = room.adults || 0;
    const roomChildren = room.childrens || 0;

    return current.remainingConfigs.filter(config => {
      const configAdults = config.qtyAdults || config.adults || 0;
      const configChildren = config.qtyChilds || config.children?.length || 0;

      return configAdults === roomAdults && configChildren === roomChildren;
    });
  }

  // Persistencia
  private saveState(state: HotelRoomState): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  private getStoredState(): HotelRoomState | null {
    const stored = localStorage.getItem(this.STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  }

  clearState(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    this.stateSubject.next(null);
  }

  // Debug
  debugState(): void {
    const current = this.getCurrentState();
    if (current) {
      console.log('=== HOTEL ROOMS STATE ===');
      console.log('Configuración original:', current.originalConfiguration);
      console.log('Configuraciones restantes:', current.remainingConfigs.length, current.remainingConfigs);
      console.log('Configuraciones satisfechas:', current.satisfiedConfigs.length, current.satisfiedConfigs);
      console.log('Habitaciones seleccionadas:', current.selectedRooms.length);

      current.selectedRooms.forEach((selection, index) => {
        console.log(`  Habitación ${index + 1}:`);
        console.log(`    - Código: ${selection.roomCode}`);
        console.log(`    - Tipo: ${selection.roomType}`);
        console.log(`    - Adultos: ${selection.qtyAdults}`);
        console.log(`    - Niños: ${selection.qtyChilds}`);
        console.log(`    - Edades: ${selection.childsage}`);
        console.log(`    - Configuración satisfecha: ${selection.satisfiedConfigIndex}`);
      });

      console.log('¿Selección completa?:', this.isSelectionComplete());
    }
  }
}
